import { Check, Clock, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { fristText, type Fristzustand } from "../format";
import { Briefgeist, type IconTyp } from "./Briefgeist";

export type ChipZustand = Fristzustand | "ki" | "akzent" | "neutral";

/* Chip-Zustaende (Handoff chipStyle): Farbe immer mit Wort und, wo es einen Zustand traegt, mit
   Symbol (nie nur Farbe). Kategorien sind neutral (Plan 6.7). */
const STIL: Record<ChipZustand, string> = {
  ueberfaellig: "bg-pg-danger-soft text-pg-danger",
  bald: "bg-pg-warn-soft text-pg-warn",
  spaeter: "bg-pg-surface-2 text-pg-text-2",
  erledigt: "bg-pg-ok-soft text-pg-ok",
  ki: "bg-pg-surface text-pg-ai",
  akzent: "bg-pg-accent-soft text-pg-accent-text",
  neutral: "bg-pg-surface-2 text-pg-text-2",
};
const ICON: Partial<Record<ChipZustand, IconTyp>> = { ueberfaellig: TriangleAlert, bald: Clock, erledigt: Check, ki: Briefgeist };

/** Chip (Handoff Schiefer, ersetzt die Kapsel-Badges): H 22, Innenabstand 8, Radius 6, Text 12/600,
 *  Symbol 14. Nie interaktiv. `icon` ueberschreibt das Symbol des Zustands, `null` laesst es weg. `umbrechen` (W64): der
 *  Text bricht an Wortgrenzen um (notfalls getrennt), statt ueber den Rand zu ragen, der Chip wird nie breiter als seine Zeile (Unsicher-Chip der
 *  Pruefkarte bei 320 px und 200 %, wie der umbrechende Chip der iOS-App); ohne bleibt er einzeilig. */
export function Chip(props: { zustand?: ChipZustand; icon?: IconTyp | null; umbrechen?: boolean; className?: string; children: ReactNode }) {
  const zustand = props.zustand ?? "neutral";
  const Icon = props.icon === null ? undefined : (props.icon ?? ICON[zustand]);
  return (
    <span
      data-zustand={zustand}
      className={`chip t-caption-1 t-caption-1-medium t-zahl inline-flex min-h-(--badge-h) items-center gap-1 rounded-chip px-2 py-(--etikett-py) ${props.umbrechen ? "max-w-full hyphens-auto" : "shrink-0 whitespace-nowrap"} ${STIL[zustand]} ${props.className ?? ""}`}
    >
      {Icon && <Icon size={14} strokeWidth={2} aria-hidden className="shrink-0" />}
      {props.children}
    </span>
  );
}

/** Frist als Chip: Zustand aus dem Datum (ueberfaellig, bald, spaeter, erledigt). Klasse
 *  `frist-badge` bleibt fuer die Pruefung bei grosser Schrift (gross.ts). */
export function FristBadge(props: { datum: string; erledigt?: boolean; praefix?: string }) {
  const { zustand, text } = fristText(props.datum, Boolean(props.erledigt));
  return (
    <Chip zustand={zustand} className="frist-badge">
      {props.praefix ? `${props.praefix} ${text}` : text}
    </Chip>
  );
}

/** Person-Etikett als Chip in Akzent-Toenung (Personenfarben nach Beitritt folgen in W22b, F12). */
export function Etikett(props: { children: string }) {
  return <Chip zustand="akzent">{props.children}</Chip>;
}

/** Roter Zaehler (Tab-Leiste, Seitenleiste): ab 1 sichtbar. */
export function Zaehler(props: { anzahl: number; label: string }) {
  if (props.anzahl < 1) return null;
  return (
    <span
      className="zaehler t-caption-1 t-zahl grid h-(--zaehler) min-w-(--zaehler) place-items-center rounded-pill bg-pg-zaehler px-1 font-bold text-pg-on-zaehler"
      aria-label={props.label}
    >
      {props.anzahl > 99 ? "99+" : props.anzahl}
    </span>
  );
}
